import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  AppBar,
  Box,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Toolbar,
  Typography,
  useMediaQuery,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import { ReactComponent as MaryIcon } from "../assets/Frame_small.svg";
import { ReactComponent as InactiveMic } from "../assets/mic_inactive.svg";
import { ReactComponent as ActiveMic } from "../assets/mic_active.svg";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { ReactComponent as InactiveHourglass } from "../assets/hourglass_inactive.svg";
import { ReactComponent as ActiveHourglass } from "../assets/hourglass_active.svg";
import { ReactComponent as InactiveChatIcon } from "../assets/chat_inactive.svg";
import { ReactComponent as ActiveChatIcon } from "../assets/chat_active.svg";
import { ReactComponent as InactiveSearchPatient } from "../assets/searchpatient_inactive.svg";
import { ReactComponent as ActiveSearchPatient } from "../assets/searchpatient_active.svg";
import { ReactComponent as MenuIcon } from "../assets/menu.svg";

import {
  Drawer,
  Grid,
  CssBaseline,
  Card,
  CardContent,
  Button,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import {
  getNameInitials,
  getTimeAgo,
  getUserLocalData,
  removeExistingSessionData,
  saveRecentChatPatientData,
} from "../lib/helper";
import LanguageSelector from "../common/LanguageSelector";

const drawerWidth = 80;
const Dashboard = ({ children, active }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [currentPath, setCurrentPath] = useState(window.location.pathname);
  const [recentChatList, setRecentChatList] = useState([]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const drawerRef = useRef(null);
  const { user_name, patient_id, session_id, token } = getUserLocalData();
  const [expanded, setExpanded] = useState(true); // Set to true to keep it open initially
  const [expandedWeb, setExpandedWeb] = useState(true); // Set to true to keep it open initially
  const [selectedChat, setSelectedChat] = useState(""); // Track selected chat

  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);

  // ✅ Function to close sidebar when clicking outside
  const handleClickOutside = (event) => {
    if (drawerRef.current && !drawerRef.current.contains(event.target)) {
      setMobileOpen(false);
    }
  };

  // ✅ Add event listener only when sidebar is open
  useEffect(() => {
    if (mobileOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [mobileOpen]);

  useEffect(() => {
    getRecentChat();
  }, []);

  const handleNavigation = (path) => {
    if (window.location.pathname === path) {
      setCurrentPath("");
      setTimeout(() => setCurrentPath(path), 0);
    } else {
      navigate(path);
    }
    setMobileOpen(false);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const onRecentClick = (text) => {
    saveRecentChatPatientData(text);
    navigate("/chat", { state: { conversationId: text?.conversation_id } });
    setMobileOpen(false);
  };

  const mainDrawer = (
    <>
      <List className="web-toolbar">
        {!isMobile && (
          <>
            <ListItem button>
              <MaryIcon />
            </ListItem>
            <ListItem
              button
              onClick={() => {
                navigate("/voice", { state: "voice" });
              }}
            >
              <Tooltip title="Start Recording" arrow>
                {active === "voice" ? <ActiveMic /> : <InactiveMic />}
              </Tooltip>
            </ListItem>
            <ListItem
              button
              onClick={() => {
                removeExistingSessionData();
                setMobileOpen(false);
                navigate("/chat", { state: { conversationId: null } });
              }}
            >
              <Tooltip title="Start New Chat" arrow>
                {active === "chat" ? <ActiveChatIcon /> : <InactiveChatIcon />}
              </Tooltip>
            </ListItem>
            <ListItem
              button
              onClick={() => {
                navigate("/voice", { state: "search" });
                localStorage.removeItem("patient_id");
                localStorage.removeItem("session_id");
                localStorage.removeItem("patient_name");
                setMobileOpen(false);
                getRecentChat();
              }}
            >
              <Tooltip title="Search Patient" arrow>
                {active == "search" ? (
                  <ActiveSearchPatient />
                ) : (
                  <InactiveSearchPatient />
                )}
              </Tooltip>
            </ListItem>
            <ListItem
              button
              onClick={() => {
                navigate("/qr-login");
                setMobileOpen(false);
              }}
            >
              <Tooltip title="QR" arrow>
                {active == "qr" ? <ActiveHourglass /> : <InactiveHourglass />}
              </Tooltip>
            </ListItem>
          </>
        )}
      </List>
      <List>
        {isMobile && (
          <>
            <Box sx={{ flexGrow: 1 }} className="appbar" component={"div"}>
              <AppBar position="static">
                <Toolbar
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    margin: "12px 20px",
                  }}
                >
                  {isMobile && (
                    <>
                      <IconButton
                        color="inherit"
                        edge="start"
                        onClick={handleDrawerToggle}
                        sx={{
                          border: "1px solid #363636",
                          width: "48px",
                          height: "48px",
                        }}
                      >
                        <MenuIcon />
                      </IconButton>
                    </>
                  )}
                </Toolbar>
              </AppBar>
              <Box sx={{ padding: "8px 32px" }}>
                {user_name && (
                  <Typography variant="fs16" className="fw-500 black-color-3">
                    Hi {user_name},
                  </Typography>
                )}

                <br />
                <Typography
                  variant="fs20"
                  className="fw-500 white-color"
                  sx={{ mt: 2 }}
                >
                  Let's see what can I do for you?
                </Typography>
              </Box>

              <Box
                sx={{
                  p: 4,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  backgroundColor: "#0E1520",
                }}
              >
                <Grid container spacing={2}>
                  {/* Left Side - Recording Card */}
                  <Grid item xs={12} sm={6}>
                    <Card sx={{ color: "white", px: 2, borderRadius: 2 }}>
                      <CardContent className="start-recording">
                        <Box
                          display="flex"
                          alignItems="center"
                          mb={2}
                          component={"div"}
                        >
                          <InactiveMic />
                        </Box>
                        <Typography
                          variant="fs14"
                          gutterBottom
                          className="fw-400 white-color"
                        >
                          Let’s Analyze patient data using voice recording
                        </Typography>
                        <Box
                          textAlign={"center"}
                          marginTop={"16px"}
                          component={"div"}
                        >
                          <Button
                            variant="contained"
                            onClick={() => handleNavigation("/voice")}
                            sx={{
                              backgroundColor: "#fff",
                              color: "#000",
                              borderRadius: "24px",
                              padding: "8px",
                              gap: "8px",
                            }}
                          >
                            <Typography
                              variant="fs12"
                              sx={{ mx: 1 }}
                              className="fw-400 black-color"
                            >
                              Start Recording
                            </Typography>
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Right Side - Action Buttons */}
                  <Grid item xs={12} sm={6}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Card
                          sx={{ color: "white", py: 1, px: 2, borderRadius: 2 }}
                          onClick={() => {
                            removeExistingSessionData();
                            setMobileOpen(false);
                            navigate("/chat", {
                              state: { conversationId: null },
                            });
                          }}
                        >
                          <CardContent className="card-content">
                            <InactiveChatIcon />
                            <Button>
                              <Typography
                                variant="fs12"
                                sx={{ mx: 1 }}
                                className="fw-400 white-color"
                              >
                                Start New Chat
                              </Typography>
                            </Button>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12}>
                        <Card
                          sx={{ color: "white", py: 0, px: 2, borderRadius: 2 }}
                          onClick={() => {
                            navigate("/voice");
                            localStorage.removeItem("patient_id");
                            localStorage.removeItem("session_id");
                            localStorage.removeItem("patient_name");
                            setMobileOpen(false);
                            getRecentChat();
                          }}
                        >
                          <CardContent className="card-content">
                            <InactiveSearchPatient />

                            <Typography
                              variant="fs12"
                              sx={{ mx: 1 }}
                              className="fw-400 white-color"
                            >
                              Search Patient
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Box>
              <Box
                sx={{
                  px: 4,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  backgroundColor: "",
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Card
                      sx={{ color: "white", py: 0, px: 2, borderRadius: 2 }}
                      onClick={() => {
                        navigate("/qr-login");
                      }}
                    >
                      <CardContent className="card-content">
                        <InactiveHourglass />

                        <Typography
                          variant="fs12"
                          sx={{ mx: 1 }}
                          className="fw-400 white-color"
                        >
                          Scan Your QR
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>
              {isMobile && (
                <Box sx={{ padding: "8px 32px" }}>
                  <Accordion
                    className="recentChats"
                    expanded={expanded}
                    onChange={() => setExpanded(!expanded)}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls="panel1-content"
                      id="panel1-header"
                    >
                      <Typography
                        component="span"
                        variant="xsSize"
                        className="font-w-700 white-color chat-heading"
                      >
                        {" "}
                        Recent Chats
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails className="acc-details">
                      <List>
                        {recentChatList?.length > 0 &&
                          recentChatList?.slice(0, 5)?.map((text, index) => (
                            <ListItem
                              key={index}
                              className={
                                selectedChat === text ? "active-chat" : ""
                              }
                            >
                              <ListItemText
                                onClick={() => {
                                  setSelectedChat(text);
                                  onRecentClick(text);
                                }}
                                primary={text?.title
                                  .replace(/(^"|"$)/g, "") // Removes the outer quotes
                                  .replace("Open Dialogue:", "") // Removes the phrase "Open Dialogue:"
                                  .trim()}
                                secondary={getTimeAgo(text?.last_updated)}
                              />
                            </ListItem>
                          ))}
                      </List>
                    </AccordionDetails>
                  </Accordion>
                </Box>
              )}
            </Box>
          </>
        )}
      </List>
    </>
  );

  //recent chats api
  const getRecentChat = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_QUERY_URL}/conversation-list`
      );

      if (response?.data) {
        const filteredData = response?.data?.filter(
          (msg) => msg.message_count > 2
        );
        setRecentChatList(filteredData);
      }
    } catch (error) {
      console.error("Error:", error?.response?.data?.detail);
    }
  };

  return (
    <>
      <Box sx={{ display: "flex", flexDirection: isMobile ? "column" : "row" }}>
        <CssBaseline />
        {/* Left Sidebar for Web */}
        {!isMobile && (
          <Drawer
            ref={drawerRef}
            variant="permanent"
            sx={{
              width: drawerWidth,
              flexShrink: 0,
              [`& .MuiDrawer-paper`]: {
                width: drawerWidth,
                boxSizing: "border-box",
                background: "#0E1520",
                color: "white",
                borderRight: "1px solid #E2E8F040",
              },
            }}
          >
            <Toolbar />
            {mainDrawer}
          </Drawer>
        )}
        {/* Sidebar for mobile only */}
        {isMobile && (
          <Drawer
            className="drawer-container"
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            handleNavigation={handleNavigation}
            ref={drawerRef}
            sx={{
              width: "100vw",
              flexShrink: 0,
              [`& .MuiDrawer-paper`]: {
                width: "100vw",
                boxSizing: "border-box",
              },
            }}
          >
            <Toolbar />
            {mainDrawer}
          </Drawer>
        )}
        {/* Main Content */}
        <Box
          component="main"
          sx={{ flexGrow: 1, display: "flex", flexDirection: "row" }}
        >
          <Box sx={{ flexGrow: 1 }} className="container" component={"div"}>
            <AppBar position="static" sx={{ backgroundColor: "#0E1520" }}>
              <Toolbar sx={{ padding: "12px 20px" }}>
                {isMobile && (
                  <IconButton
                    color="inherit"
                    edge="start"
                    onClick={handleDrawerToggle}
                  >
                    <MenuIcon />
                  </IconButton>
                )}
                {!isMobile && (
                  <Typography
                    variant="lSize"
                    className="font-w-800 white-color"
                  >
                    MARY
                  </Typography>
                )}
                <Box sx={{ flexGrow: 1 }} />
                <LanguageSelector variant="menu" />
              </Toolbar>
            </AppBar>
            {/* Chat Window */}

            {children}
          </Box>
          {/* Right Sidebar for Web */}
          {!isMobile && (
            <Box
              sx={{
                minWidth: "300px !important",
                width: "300px",
                bgcolor: "#1A212B",
                py: 2,
                color: "white",
                height: "100vh",
              }}
            >
              <Box
                borderBottom={1}
                borderColor="#E2E8F040"
                display="flex"
                flexDirection="row"
                alignItems="center"
                p={2}
              >
                {user_name && (
                  <>
                    <Box component={"div"} className="user-name">
                      <Typography className="iniatial-name">
                        {getNameInitials(user_name)}
                      </Typography>
                    </Box>

                    <Typography
                      variant="xxsSize"
                      className="font-w-700 white-color"
                      ml={2}
                    >
                      {user_name}
                    </Typography>
                  </>
                )}
              </Box>
              {/* {recentChats} */}
              <Accordion
                className="recentChats"
                expanded={expandedWeb}
                onChange={() => setExpandedWeb(!expandedWeb)}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1-content"
                  id="panel1-header"
                >
                  <Typography
                    component="span"
                    variant="xsSize"
                    className="font-w-700 white-color chat-heading"
                  >
                    {" "}
                    Recent Chats
                  </Typography>
                </AccordionSummary>
                <AccordionDetails className="acc-details">
                  <List>
                    {recentChatList?.length > 0 &&
                      recentChatList?.slice(0, 5)?.map((text, index) => (
                        <ListItem
                          key={index}
                          className={selectedChat === text ? "active-chat" : ""}
                        >
                          <ListItemText
                            onClick={() => {
                              setSelectedChat(text);
                              onRecentClick(text);
                            }}
                            primary={text?.title
                              .replace(/(^"|"$)/g, "") // Removes the outer quotes
                              .replace("Open Dialogue:", "") // Removes the phrase "Open Dialogue:"
                              .trim()}
                            secondary={getTimeAgo(text?.last_updated)}
                          />
                        </ListItem>
                      ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};

export default Dashboard;
